// NotificationModal.jsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Dialog, DialogContent } from "@material-ui/core";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAPI } from "../context/ApiContext";

const ellipsize = (s, n = 80) => (!s ? "" : s.length > n ? s.slice(0, n) + "…" : s);
const fmtDateHeader = (iso) => { try { return new Date(iso).toLocaleDateString(); } catch { return ""; } };

/* ---------- 相対時刻（日本語寄り） ---------- */
const formatRelativeJP = (date, base = new Date()) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  let diff = Math.floor((base - date) / 1000);
  if (diff < 0) diff = 0;
  if (diff < 10) return "たった今";
  if (diff < 60) return `${diff}秒前`;
  const m = Math.floor(diff / 60);
  if (m < 60) return `${m}分前`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}時間前`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}日前`;
  return date.toLocaleDateString(undefined, { year: "numeric", month: "numeric", day: "numeric" });
};

const useRelativeTime = (iso) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);
  return useMemo(() => {
    try { return formatRelativeJP(new Date(iso), new Date(now)); }
    catch { return ""; }
  }, [iso, now]);
};

/* ===================== NotificationItem ===================== */
/* ★ 役者(actor)アイコン・名前を表示。previewTextはサーバの値を優先。 */
const AVATAR_FALLBACK = "https://tatami.site/default_user_image.jpg";

const NotificationItem = ({ noti, onClick }) => {
  const { t } = useTranslation();
  const isUnread = !noti.read;

  const actorNick = noti.actor?.nickname || "";
  const actorImg  = noti.actor?.image || AVATAR_FALLBACK;

  const label = useMemo(() => {
    switch (noti.notiType) {
      case "niced":            return t("NotificationModal1")     || "Your post got a Nice";
      case "reposted":         return t("NotificationModal2")     || "Your post was reposted";
      case "commented":        return t("NotificationModal3")     || "New comment on your post";
      case "commented_thread": return t("NotificationModal6")     || "New comment on a post you commented";
      case "followed":         return t("NotificationModal4")     || "You have a new follower";
      default:                 return t("NotificationModal5")     || "Notification";
    }
  }, [noti.notiType, t]);

  /* タイトル行は actor がいれば「<名前> が <ラベル>」の簡潔表記 */
  const titleText = actorNick ? `${actorNick} — ${label}` : label;

  const meta = useMemo(() => {
    switch (noti.notiType) {
      case "niced":            return { icon: "❤️", cls: "nice",    badge: "NICE" };
      case "reposted":         return { icon: "🔁", cls: "repost",  badge: "REPOST" };
      case "commented":        return { icon: "💬", cls: "comment", badge: "COMMENT" };
      case "commented_thread": return { icon: "💬", cls: "thread",  badge: "THREAD" };
      case "followed":         return { icon: "👤", cls: "follow",  badge: "FOLLOW" };
      default:                 return { icon: "🔔", cls: "default", badge: "NOTICE" };
    }
  }, [noti.notiType]);

  const createdAtShort = useRelativeTime(noti.created_at);
  const createdAtFull = useMemo(() => {
    try { return new Date(noti.created_at).toLocaleString(); }
    catch { return noti.created_at || ""; }
  }, [noti.created_at]);

  /* ★ サーバの previewText を最優先。無ければ relatedPost から拾う（後方互換） */
  const postPreview = useMemo(() => {
    const s = (noti.previewText || "").trim();
    if (s) return ellipsize(s, 120);
    const text = noti.relatedPost?.postText || "";
    return ellipsize(text, 120);
  }, [noti.previewText, noti.relatedPost]);

  return (
    <div
      className={`nl-noti ${isUnread ? "is-unread" : ""}`}
      onClick={() => onClick(noti)}
      role="button"
      aria-label="notification-item"
    >
      {/* 左：アバター + 小バッジ */}
      <div className="nl-noti__avatarWrap">
        <img className="nl-noti__avatar" src={actorImg} alt="" />
        <span className={`nl-noti__badgeIcon ${meta.cls}`} title={meta.badge}>{meta.icon}</span>
      </div>

      {/* タイトル */}
      <div className="nl-noti__title">
        <span className="nl-noti__titleText">{titleText}</span>
      </div>

      {/* 右メタ（バッジ+時刻） */}
      <div className="nl-noti__meta">
        <span className="nl-noti__badge">{meta.badge}</span>
        <span className="nl-noti__time" title={createdAtFull}>{createdAtShort}</span>
        {isUnread && <span className="nl-noti__dot" />}
      </div>

      {!!postPreview && <div className="nl-noti__preview">{postPreview}</div>}
    </div>
  );
};

/* ===================== NotificationModal ===================== */
const NotificationModal = ({ isVisible, handleClose, onUnreadChange }) => {
  const { t } = useTranslation();
  const api = useAPI();
  const history = useHistory();

  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [noMore, setNoMore] = useState(false);

  const scrollContainerRef = useRef(null);
  const loaderRef = useRef(null);

  const isLoadingRef = useRef(false);
  const noMoreRef = useRef(false);
  useEffect(() => { isLoadingRef.current = isLoading; }, [isLoading]);
  useEffect(() => { noMoreRef.current = noMore; }, [noMore]);

  /* ★ 余計な補完フェッチを撤廃（バックエンドが previewText/actor/relatedPostId を返すため） */

  // ---- 取得
  const fetchPage = useCallback(
    async (p, { force = false } = {}) => {
      if (!api?.me?.userId) return;
      if (!force && (isLoadingRef.current || noMoreRef.current)) return;

      setIsLoading(true);
      try {
        const res = await api.getNotifications(api.me.userId, { count: p });
        const list = Array.isArray(res?.res) ? res.res : [];
        const total = typeof res?.totalCount === "number" ? res.totalCount : undefined;

        setRows((prev) => (p === 0 ? list : [...prev, ...list]));

        const PAGE_SIZE = 20;
        if (total !== undefined) {
          const loaded = (p + 1) * PAGE_SIZE;
          setNoMore(list.length === 0 || loaded >= total);
        } else {
          setNoMore(list.length === 0);
        }
      } catch {
        setNoMore(true);
      } finally {
        setIsLoading(false);
      }
    },
    [api]
  );

  // ---- open 初期化
  useEffect(() => {
    if (!isVisible) return;
    setRows([]);
    setPage(0);
    setNoMore(false);
    fetchPage(0, { force: true });
  }, [isVisible, fetchPage]);

  // ---- ページ進行
  useEffect(() => {
    if (!isVisible) return;
    if (page === 0) return;
    fetchPage(page);
  }, [page, isVisible, fetchPage]);

  // ---- IO（無限スクロール）
  useEffect(() => {
    if (!isVisible) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isLoadingRef.current && !noMoreRef.current) {
          setPage((v) => v + 1);
        }
      },
      { root: scrollContainerRef.current, rootMargin: "200px", threshold: 0.1 }
    );
    const el = loaderRef.current;
    if (el) io.observe(el);
    return () => io.disconnect();
  }, [isVisible]);

  // ---- 未読数
  useEffect(() => {
    onUnreadChange?.(rows.reduce((acc, n) => acc + (n.read ? 0 : 1), 0));
  }, [rows, onUnreadChange]);

  // ---- クリック：既読 & 遷移
  const handleClick = useCallback(
    async (noti) => {
      const notiId = noti.notiId ?? noti.id;

      if (!noti.read && notiId) {
        setRows((prev) => prev.map((n) => ((n.notiId ?? n.id) === notiId ? { ...n, read: true } : n)));
        try { await api.markNotificationRead(notiId); }
        catch {
          setRows((prev) => prev.map((n) => ((n.notiId ?? n.id) === notiId ? { ...n, read: false } : n)));
        }
      }

      let to = null;
      if (noti.notiType === "followed") {
        /* ★ actor があれば、そのユーザー詳細へ */
        const uid = noti.actor?.userId
          || noti.relatedFollow?.followedByUser?.userId
          || noti.relatedFollow?.follower?.userId
          || null;
        if (uid) {
          to = `/user/${encodeURIComponent(uid)}`;
        } else if (api?.me?.userId) {
          to = `/connections?user_id=${encodeURIComponent(api.me.userId)}&tab=followers`;
        }
      } else {
        /* ★ まず relatedPostId（サーバ拡張）→ 後方互換 */
        const pid =
          noti.relatedPostId
          || noti.relatedPost?.postId
          || noti.relatedPost
          || noti.post?.postId
          || noti.post
          || null;
        if (pid) to = `/post/${encodeURIComponent(pid)}`;
      }

      if (to) {
        handleClose?.();
        history.push(to);
      }
    },
    [api, history, handleClose]
  );

  // ---- 日付グループ
  const grouped = useMemo(() => {
    const map = new Map();
    for (const n of rows) {
      const key = fmtDateHeader(n.created_at);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(n);
    }
    return Array.from(map.entries());
  }, [rows]);

  return (
    <>
      <style>{`
    /* ===== レイアウト ===== */
    .nl-noti {
      display: grid;
      grid-template-columns: 36px 1fr auto;
      grid-template-areas:
        "avatar title meta"
        "avatar preview meta";
      column-gap: 10px;
      row-gap: 4px;
      padding: 10px 12px;
      border: 1px solid #eef1f4;
      border-radius: 12px;
      background: #fff;
      transition: box-shadow .12s ease, transform .04s ease, background .12s ease;
      cursor: pointer;
    }
    .nl-noti + .nl-noti { margin-top: 8px; }
    .nl-noti:hover { box-shadow: 0 2px 12px rgba(17,24,39,.06); background: #fcfcfd; }
    .nl-noti.is-unread { border-color: #dce7ff; background: #f7faff; }

    /* 左：アバター */
    .nl-noti__avatarWrap { grid-area: avatar; position: relative; width: 36px; height: 36px; }
    .nl-noti__avatar {
      width: 36px; height: 36px; border-radius: 9999px; object-fit: cover;
      background: #e5e7eb;
      display: block;
    }
    .nl-noti__badgeIcon {
      position: absolute; right: -4px; bottom: -4px;
      width: 18px; height: 18px; border-radius: 9999px;
      display: inline-flex; align-items: center; justify-content: center;
      font-size: 11px; line-height: 1; color: #fff; border: 2px solid #fff;
    }
    .nl-noti__badgeIcon.nice   { background: #ef476f; }
    .nl-noti__badgeIcon.repost { background: #06d6a0; }
    .nl-noti__badgeIcon.comment{ background: #118ab2; }
    .nl-noti__badgeIcon.thread { background: #3a86ff; }
    .nl-noti__badgeIcon.follow { background: #8338ec; }
    .nl-noti__badgeIcon.default{ background: #64748b; }

    /* タイトル */
    .nl-noti__title { grid-area: title; min-width: 0; }
    .nl-noti__titleText {
      font-size: 12px; font-weight: 700; color: #111827;
      display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
      overflow: hidden;
    }

    /* 右端メタ */
    .nl-noti__meta {
      grid-area: meta;
      display: inline-flex; align-items: center; gap: 8px;
      white-space: nowrap; justify-content: flex-end;
    }
    .nl-noti__badge {
      font-size: 10px; font-weight: 700;
      padding: 2px 8px; border-radius: 999px; letter-spacing: .02em;
      background: #eef2ff; color: #3730a3;
    }
    .nl-noti__time { font-size: 10px; color: #6b7280; }
    .nl-noti__dot { width: 8px; height: 8px; border-radius: 9999px; background: #0ea5e9; }

    /* プレビュー */
    .nl-noti__preview {
      grid-area: preview;
      font-size: 11px; color: #374151;
      display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical;
      overflow: hidden; text-overflow: ellipsis;
    }

    /* 日付セクション */
    .nl-noti__section { font-size: 11px; color: #6b7280; margin: 10px 0 6px 2px; }

    /* モバイル最適化 */
    @media (max-width: 480px) {
      .nl-noti { padding: 8px 10px; row-gap: 2px; grid-template-columns: 32px 1fr auto; }
      .nl-noti__avatarWrap { width: 32px; height: 32px; }
      .nl-noti__avatar { width: 32px; height: 32px; }
      .nl-noti__badge { display: none; }
      .nl-noti__titleText { -webkit-line-clamp: 3; font-size: 11.5px; }
      .nl-noti__time { font-size: 9.5px; }
    }
  `}</style>

      <Dialog open={isVisible} onClose={handleClose} fullWidth maxWidth="sm" style={{ maxHeight: "100vh" }}>
        <DialogContent
          ref={scrollContainerRef}
          style={{ minHeight: "70vh", maxHeight: "100vh", overflowY: "auto", position: "relative" }}
        >
          <div className="position-absolute right-0 top-5 pointer me-3" onClick={handleClose}>
            <i className="ti-close text-grey-400 fw-700 font-xsss"></i>
          </div>

          {/* リスト */}
          {grouped.length === 0 && !isLoading && (
            <div className="card w-100 shadow-xss rounded-xxl border-0 p-4 mb-3 text-center text-grey-500">
              {t("NoResults") || "通知はありません"}
            </div>
          )}

          {grouped.map(([label, list]) => (
            <div key={label}>
              <div className="nl-noti__section">{label}</div>
              {list.map((n) => (
                <NotificationItem
                  key={n.notiId ?? n.id}
                  noti={n}
                  onClick={handleClick}
                />
              ))}
            </div>
          ))}

          <div ref={loaderRef} style={{ height: 1 }} />

          {isLoading && (
            <div className="card-body p-0 mb-3">
              <div className="card w-100 shadow-xss rounded-xxl border-0 p-4 mb-3">
                <div className="snippet mt-2 ms-auto me-auto" data-title=".dot-typing">
                  <div className="stage"><div className="dot-typing" /></div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NotificationModal;
